/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { v4 as uuidv4 } from 'uuid';
import {
  CmdLabel,
  CmdLoader,
  CmdModal,
  CmdModalFooterButton,
} from '@commander-services/gui-components';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../Services/FormikValidatorService';
import { IFormModalCustomProps } from '../Forms/FormModal';
import { ICheckboxGroupOption } from '../Forms/ArrayFields/CheckboxGroup';
import { IMultipleSelectOption } from '../Forms/ArrayFields/MultiSelect';
import { DATE_SPAN, TIME_SPAN, DAYS_OF_WEEK, DAYS_MONTHS } from './constants';
import { IChipInputItem } from '../Forms/Fields/ChipInput';
import FormikService from '../../Services/FormikService';
import { IOption } from '../Forms/CmdField';
import {
  IAlarmTimeFrame,
  ITimeFrame,
  ITimeFrameDateTimeSpan,
  ITimeFrameDaysMonths,
  ITimeFrames,
  TimeFrameDataType,
  TimeFrameFormDataType,
} from './types';
import SimpleSelectHookForm from '../Forms/ArrayFields/SimpleSelectHookForm';
import Calendar from '../Forms/ReactHookFormFields/Calendar';
import CheckboxGroupArrayHookForm from '../Forms/ArrayFields/CheckboxGroupArrayHookForm';
import MultiSelectHookForm from '../Forms/ArrayFields/MultiSelectHookForm';
import {
  createDays,
  getDayInWeekValues,
  getMonthsValues,
  getTimeFrameValidation,
  getTimeFrameValues,
  validate,
} from './AlarmTimeFrameService';
import useAnalytics from '../../hooks/useAnalytics';

interface ITimeFrameFormProps {
  closeCallback: () => void;
  responseCallback: (values: IChipInputItem, valuesToBE: TimeFrameDataType[]) => void;
  id?: number | string | null;
  data?: TimeFrameFormDataType;
  openCallback: () => void;
}

export default function TimeFrameForm(props: ITimeFrameFormProps) {
  dayjs.extend(utc);
  const intl = useIntl();
  const { formatMessage: f } = intl;
  const [isNextForm, setIsNextForm] = React.useState<boolean>(false);
  const [timeFrameValues, setTimeFrameValues] = React.useState<IOption[]>([]);
  const [monthValues, setMonthValues] = React.useState<IMultipleSelectOption[]>([]);
  const [daysOfWeekValues, setDaysOfWeekValues] = React.useState<ICheckboxGroupOption[]>([]);
  const [timeFrameValidation, setTimeFrameValidation] =
    React.useState<IValidationApiResponse | null>(null);
  const [daysOfMonthValues, setDaysOfMonthValues] = React.useState<IMultipleSelectOption[]>([]);
  const { trackEvent } = useAnalytics();

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
    reset,
    formState: { errors },
  } = useForm<ITimeFrames>({
    mode: 'onChange',
    defaultValues: {
      timeFrames: [
        {
          timeFrame: '',
          dateSpan: [],
          timeFrom: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
          timeTo: new Date(new Date().setHours(23, 59, 0, 0)).toISOString(),
          daysOfWeek: [],
          days: [],
          months: [],
        },
      ],
    },

    resolver: async (formData) => {
      const formValidation: IValidationApiResponse = {
        ...timeFrameValidation,
        timeFrame: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
      };

      const tempErrors = validate(formData, formValidation, intl);
      // Initialize an empty object to accumulate the transformed errors
      const formErrors: Record<string, { type: string; message: string }> = {};

      // Check if there are errors for the timeFrames array
      if (tempErrors.timeFrames && Array.isArray(tempErrors.timeFrames)) {
        tempErrors.timeFrames.forEach((errorObj, index) => {
          // Iterate over each error object in the timeFrames array
          Object.keys(errorObj).forEach((key) => {
            // For each key in the error object, transform it into the React Hook Form expected format

            const fieldName = `timeFrames.${index}.${key}`;
            formErrors[fieldName] = {
              type: 'validation',
              message: errorObj[key],
            };
          });
        });
      }

      return {
        values: Object.keys(formErrors).length ? {} : formData, // If there are errors, values are discarded
        errors: formErrors,
      };
    },
  });
  const { fields, append, remove, update } = useFieldArray({
    name: 'timeFrames',
    control,
    rules: { maxLength: 4 },
  });

  // Fetch Time Frame Values
  const fetchTimeFrameValues = async () => {
    const values = await getTimeFrameValues();
    setTimeFrameValues(values || []);
    return values || [];
  };

  // Fetch Months Values
  const fetchMonthsValues = async () => {
    const values = await getMonthsValues();
    setMonthValues(values || []);
    return values || [];
  };

  // Fetch Days Of Week Values
  const fetchDaysOfWeekValues = async () => {
    const values = await getDayInWeekValues();
    setDaysOfWeekValues(values || []);
    return values || [];
  };

  // Fetch Time Frame Validation
  const fetchTimeFrameValidation = async () => {
    const response = await getTimeFrameValidation();

    setTimeFrameValidation(response || null);
    setDaysOfMonthValues(createDays(31));
    return response || null;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['timeFrameValuesKey'],
        queryFn: () => fetchTimeFrameValues(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['timeFrameMonthValues'],
        queryFn: () => fetchMonthsValues(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['timeFrameDaysOfWeekValues'],
        queryFn: () => fetchDaysOfWeekValues(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['timeFrameValidation'],
        queryFn: () => fetchTimeFrameValidation(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingTimeFrameValues } = results[0];
  const { isLoading: isLoadingMonthValues } = results[1];
  const { isLoading: isLoadingDaysOfWeekValues } = results[2];
  const { isLoading: isLoadingTimeFrameValidation } = results[3];

  const isLoading =
    isLoadingTimeFrameValues ||
    isLoadingMonthValues ||
    isLoadingDaysOfWeekValues ||
    isLoadingTimeFrameValidation;

  // Create Days of Month Values
  // const daysOfMonthValues = React.useMemo(() => {
  //   return createDays(31);
  // }, []);

  const getHours = (date: Date) => {
    // Ensure the date is a dayjs object
    const d = dayjs(date);
    // Format the hours with leading zero if necessary
    const hours = d.format('HH');
    return hours;
  };

  const getMinutes = (date: Date) => {
    // Ensure the date is a dayjs object
    const d = dayjs(date);
    // Format the minutes with leading zero if necessary
    const minutes = d.format('mm');
    return minutes;
  };

  const handleOnSubmit = (values: ITimeFrames) => {
    const timeFrames: TimeFrameDataType[] = [];
    let labelForChipInput = '';
    let valuesForSend: TimeFrameDataType;
    const valuesForChipInput = {
      id: props.id ? String(props.id) : uuidv4(),
      label: '',
    };

    const tempTimeFrames = Object.assign(values.timeFrames);

    tempTimeFrames.forEach((item: any) => {
      if (item.timeFrame === DAYS_OF_WEEK && item.daysOfWeek.length > 0) {
        let tempDaysLabel = '';
        let daysOfWeekLabel = '';

        if (item.daysOfWeek.length === daysOfWeekValues.length) {
          tempDaysLabel += `${daysOfWeekValues[0].item} - ${
            daysOfWeekValues[daysOfWeekValues.length - 1].item
          } `;
        } else {
          let count = -1;

          const filtredArray = item.daysOfWeek.sort();

          filtredArray.forEach((items: string, index: number) => {
            if (items === '0') {
              filtredArray[index] = f({
                id: 'alarms.modal.timeFrame.days.of.week.value',
              });
            }
          });

          const sortArray = filtredArray.sort();
          let tempValue = Number(sortArray[0]);

          daysOfWeekValues.forEach((dayLabel: any, indexValue: number) => {
            if (dayLabel.value === 0) {
              daysOfWeekValues[indexValue].value = f({
                id: 'alarms.modal.timeFrame.days.of.week.value',
              });
            }
          });

          sortArray.forEach((days: string, index: number) => {
            daysOfWeekValues.forEach((dayLabel: any, indexValue: number) => {
              if (days === String(dayLabel.value)) {
                if (Number(days) - tempValue === 0) {
                  tempDaysLabel += `${dayLabel.item}`;
                } else if (Number(days) - tempValue === 1) {
                  count = indexValue;

                  if (index === sortArray.length - 1) {
                    if (sortArray.length === 2) {
                      tempDaysLabel += `, ${dayLabel.item}`;
                    } else {
                      tempDaysLabel += ` - ${dayLabel.item}`;
                    }
                  }
                } else if (Number(days) - tempValue > 1) {
                  if (count >= 0) {
                    tempDaysLabel += ` - ${daysOfWeekValues[count].item}, ${dayLabel.item}`;

                    count = -1;
                  } else {
                    tempDaysLabel += `, ${dayLabel.item}`;
                  }
                }
              }
            });
            tempValue = Number(days);
          });
        }

        const tempDaysOfWeek: string[] = [];

        item.daysOfWeek.forEach((day: string) => {
          if (day === '7') {
            tempDaysOfWeek.push('0');
          } else {
            tempDaysOfWeek.push(day);
          }
        });

        if (timeFrames.length > 0) {
          labelForChipInput += ` + ${f(
            {
              id: 'alarms.modal.timeFrame.days.of.week',
            },
            { days: tempDaysLabel }
          )}`;
          daysOfWeekLabel += ` + ${f(
            {
              id: 'alarms.modal.timeFrame.days.of.week',
            },
            { days: tempDaysLabel }
          )}`;
        } else {
          labelForChipInput += ` ${f(
            {
              id: 'alarms.modal.timeFrame.days.of.week',
            },
            { days: tempDaysLabel }
          )} `;
          daysOfWeekLabel += ` ${f(
            {
              id: 'alarms.modal.timeFrame.days.of.week',
            },
            { days: tempDaysLabel }
          )} `;
        }
        valuesForSend = {
          recordId: valuesForChipInput.id,
          type: item.timeFrame,
          label: daysOfWeekLabel.trim(),
          value: tempDaysOfWeek,
        };
        timeFrames.push(valuesForSend);
      } else if (item.timeFrame === DAYS_MONTHS && item.days.length > 0 && item.months.length > 0) {
        let tempDaysLabel = '';
        let tempMonthsLabel = '';

        let daysMonthsLabel = '';

        if (
          item.days.length === daysOfMonthValues.length &&
          item.months.length === monthValues.length
        ) {
          tempMonthsLabel += ` ${monthValues[0].item} - ${
            monthValues[monthValues.length - 1].item
          }  `;
          tempDaysLabel = `${daysOfMonthValues[0].value}-${
            daysOfMonthValues[daysOfMonthValues.length - 1].value
          }  `;
        } else {
          let countDays = -1;
          let countMonth = -1;

          const sortDays = item.days.sort((a: number, b: number) => a - b);
          let tempValueDays = Number(sortDays[0]);
          const sortMonths = item.months.sort((a: number, b: number) => a - b);
          let tempValueMonths = Number(sortMonths[0]);

          sortDays.forEach((days: string, index: number) => {
            daysOfMonthValues.forEach((daysMonth: IMultipleSelectOption, indexValue: number) => {
              if (days === String(daysMonth.value)) {
                if (Number(days) - tempValueDays === 0) {
                  tempDaysLabel += `${daysMonth.item}`;
                } else if (Number(days) - tempValueDays === 1) {
                  countDays = indexValue;

                  if (index === sortDays.length - 1) {
                    if (sortDays.length === 2) {
                      tempDaysLabel += `, ${daysMonth.item}`;
                    } else {
                      tempDaysLabel += ` - ${daysMonth.item}`;
                    }
                  }
                } else if (Number(days) - tempValueDays > 1) {
                  if (countDays >= 0) {
                    tempDaysLabel += ` - ${daysOfMonthValues[countDays].item}, ${daysMonth.item}`;

                    countDays = -1;
                  } else {
                    tempDaysLabel += `, ${daysMonth.item}`;
                  }
                }
              }
            });
            tempValueDays = Number(days);
          });

          sortMonths.forEach((selectedMonths: string, index: number) => {
            monthValues.forEach((months: IMultipleSelectOption, indexValue: number) => {
              if (selectedMonths === String(months.value)) {
                if (Number(selectedMonths) - tempValueMonths === 0) {
                  tempMonthsLabel += `${months.item}`;
                } else if (Number(selectedMonths) - tempValueMonths === 1) {
                  countMonth = indexValue;

                  if (index === sortMonths.length - 1) {
                    if (sortMonths.length === 2) {
                      tempMonthsLabel += `, ${months.item}`;
                    } else {
                      tempMonthsLabel += ` - ${months.item}`;
                    }
                  }
                } else if (Number(selectedMonths) - tempValueMonths > 1) {
                  if (countMonth >= 0) {
                    tempMonthsLabel += ` - ${monthValues[countMonth].item}, ${months.item}`;

                    countMonth = -1;
                  } else {
                    tempMonthsLabel += `, ${months.item}`;
                  }
                }
              }
            });
            tempValueMonths = Number(selectedMonths);
          });
        }

        if (item.days.length === 1 && item.months.length === 1) {
          if (timeFrames.length > 0) {
            labelForChipInput += ` + ${f(
              { id: 'alarms.modal.timeFrame.days.months.every.year' },
              { date: `${tempDaysLabel}.${item.months}` }
            )} `;
            daysMonthsLabel += ` + ${f(
              { id: 'alarms.modal.timeFrame.days.months.every.year' },
              { date: `${tempDaysLabel}.${item.months}` }
            )} `;
          } else {
            labelForChipInput += `${f(
              { id: 'alarms.modal.timeFrame.days.months.every.year' },
              { date: `${tempDaysLabel}.${item.months}` }
            )}`;
            daysMonthsLabel += `${f(
              { id: 'alarms.modal.timeFrame.days.months.every.year' },
              { date: `${tempDaysLabel}.${item.months}` }
            )}`;
          }
        } else if (timeFrames.length > 0) {
          labelForChipInput += ` + ${f(
            { id: 'alarms.modal.timeFrame.days.monthsAndDays' },
            { months: tempMonthsLabel, days: tempDaysLabel }
          )} `;
          daysMonthsLabel += ` + ${f(
            { id: 'alarms.modal.timeFrame.days.monthsAndDays' },
            { months: tempMonthsLabel, days: tempDaysLabel }
          )} `;
        } else {
          labelForChipInput += `${f(
            { id: 'alarms.modal.timeFrame.days.monthsAndDays' },
            { months: tempMonthsLabel, days: tempDaysLabel }
          )}`;
          daysMonthsLabel += `${f(
            { id: 'alarms.modal.timeFrame.days.monthsAndDays' },
            { months: tempMonthsLabel, days: tempDaysLabel }
          )}`;
        }

        valuesForSend = {
          recordId: valuesForChipInput.id,
          type: item.timeFrame,
          label: daysMonthsLabel.trim(),
          value: {
            days: item.days,
            months: item.months,
          },
        };
        timeFrames.push(valuesForSend);
      } else if (item.timeFrame === DAYS_MONTHS && item.days.length > 0) {
        let tempDaysLabel = '';

        let countDays = -1;

        let daysMonthsLabel = '';

        const sortDays = item.days.sort((a: number, b: number) => a - b);
        let tempValueDays = Number(sortDays[0]);

        sortDays.forEach((days: string, index: number) => {
          daysOfMonthValues.forEach((daysMonth: IMultipleSelectOption, indexValue: number) => {
            if (days === String(daysMonth.value)) {
              if (Number(days) - tempValueDays === 0) {
                tempDaysLabel += `${daysMonth.item}`;
              } else if (Number(days) - tempValueDays === 1) {
                countDays = indexValue;

                if (index === sortDays.length - 1) {
                  if (sortDays.length === 2) {
                    tempDaysLabel += `, ${daysMonth.item}`;
                  } else {
                    tempDaysLabel += ` - ${daysMonth.item}`;
                  }
                }
              } else if (Number(days) - tempValueDays > 1) {
                if (countDays >= 0) {
                  tempDaysLabel += ` - ${daysOfMonthValues[countDays].item}, ${daysMonth.item}`;

                  countDays = -1;
                } else {
                  tempDaysLabel += `, ${daysMonth.item}`;
                }
              }
            }
          });
          tempValueDays = Number(days);
        });

        if (item.days.length === 1) {
          if (timeFrames.length > 0) {
            labelForChipInput += ` + ${f({
              id: 'alarms.modal.timeFrame.days.day.every',
            })} ${tempDaysLabel}.${f({
              id: 'alarms.modal.timeFrame.days.day.every.day.month',
            })} `;
            daysMonthsLabel += ` + ${f({
              id: 'alarms.modal.timeFrame.days.day.every',
            })} ${tempDaysLabel}.${f({
              id: 'alarms.modal.timeFrame.days.day.every.day.month',
            })} `;
          } else {
            labelForChipInput += `${f({
              id: 'alarms.modal.timeFrame.days.day.every',
            })} ${tempDaysLabel}.${f({
              id: 'alarms.modal.timeFrame.days.day.every.day.month',
            })}`;
            daysMonthsLabel += `${f({
              id: 'alarms.modal.timeFrame.days.day.every',
            })} ${tempDaysLabel}.${f({
              id: 'alarms.modal.timeFrame.days.day.every.day.month',
            })}`;
          }
        } else if (timeFrames.length > 0) {
          labelForChipInput += ` + ${f({
            id: 'alarms.modal.timeFrame.days.day.every.month',
          })} ${tempDaysLabel} ${f({
            id: 'alarms.modal.timeFrame.days.day.every.month.day',
          })} `;
          daysMonthsLabel += ` + ${f({
            id: 'alarms.modal.timeFrame.days.day.every.month',
          })} ${tempDaysLabel} ${f({
            id: 'alarms.modal.timeFrame.days.day.every.month.day',
          })} `;
        } else {
          labelForChipInput += `${f({
            id: 'alarms.modal.timeFrame.days.day.every.month',
          })} ${tempDaysLabel} ${f({
            id: 'alarms.modal.timeFrame.days.day.every.month.day',
          })}`;
          daysMonthsLabel += `${f({
            id: 'alarms.modal.timeFrame.days.day.every.month',
          })} ${tempDaysLabel} ${f({
            id: 'alarms.modal.timeFrame.days.day.every.month.day',
          })}`;
        }
        valuesForSend = {
          recordId: valuesForChipInput.id,
          type: item.timeFrame,
          label: daysMonthsLabel.trim(),
          value: {
            days: item.days,
            months: [],
          },
        };
        timeFrames.push(valuesForSend);
      } else if (item.timeFrame === DAYS_MONTHS && item.months.length > 0) {
        let tempMonthsLabel = '';
        let countMonth = -1;
        let daysMonthsLabel = '';

        const sortMonths = item.months.sort((a: number, b: number) => a - b);
        let tempValueMonths = Number(sortMonths[0]);

        sortMonths.forEach((selectedMonths: string, index: number) => {
          monthValues.forEach((months: IMultipleSelectOption, indexValue: number) => {
            if (selectedMonths === String(months.value)) {
              if (Number(selectedMonths) - tempValueMonths === 0) {
                tempMonthsLabel += `${months.item}`;
              } else if (Number(selectedMonths) - tempValueMonths === 1) {
                countMonth = indexValue;

                if (index === sortMonths.length - 1) {
                  if (sortMonths.length === 2) {
                    tempMonthsLabel += `, ${months.item}`;
                  } else {
                    tempMonthsLabel += ` - ${months.item}`;
                  }
                }
              } else if (Number(selectedMonths) - tempValueMonths > 1) {
                if (countMonth >= 0) {
                  tempMonthsLabel += ` - ${monthValues[countMonth].item}, ${months.item}`;

                  countMonth = -1;
                } else {
                  tempMonthsLabel += `, ${months.item} `;
                }
              }
            }
          });
          tempValueMonths = Number(selectedMonths);
        });

        if (timeFrames.length > 0) {
          labelForChipInput += ` + ${f({
            id: 'alarms.modal.timeFrame.days.month.alone',
          })}: ${tempMonthsLabel} `;
          daysMonthsLabel += ` + ${f({
            id: 'alarms.modal.timeFrame.days.month.alone',
          })}: ${tempMonthsLabel} `;
        } else {
          labelForChipInput += `${f({
            id: 'alarms.modal.timeFrame.days.month.alone',
          })}: ${tempMonthsLabel}`;
          daysMonthsLabel += `${f({
            id: 'alarms.modal.timeFrame.days.month.alone',
          })}: ${tempMonthsLabel}`;
        }

        valuesForSend = {
          recordId: valuesForChipInput.id,
          type: item.timeFrame,
          label: daysMonthsLabel.trim(),
          value: {
            days: [],
            months: item.months,
          },
        };
        timeFrames.push(valuesForSend);
      } else if (item.timeFrame === DATE_SPAN && item.dateSpan.length > 0) {
        const dateFrom = dayjs(item.dateSpan[0]).format('DD. MM. YYYY');
        const dateTo = dayjs(item.dateSpan[1]).format('DD. MM. YYYY');
        let dateSpanLabel = '';

        if (dateFrom === dateTo) {
          if (timeFrames.length > 0) {
            labelForChipInput += ` + ${f(
              { id: 'alarms.modal.timeFrame.date.indefinite' },
              { dateFrom, dateTo }
            )} `;
            dateSpanLabel += ` + ${f(
              { id: 'alarms.modal.timeFrame.date.indefinite' },
              { dateFrom, dateTo }
            )} `;
          } else {
            labelForChipInput += ` ${f(
              { id: 'alarms.modal.timeFrame.date.indefinite' },
              { dateFrom, dateTo }
            )}`;
            dateSpanLabel += ` ${f(
              { id: 'alarms.modal.timeFrame.date.indefinite' },
              { dateFrom, dateTo }
            )}`;
          }
        } else if (timeFrames.length > 0) {
          labelForChipInput += ` + ${f(
            { id: 'alarms.modal.timeFrame.date' },
            { dateFrom, dateTo }
          )} `;
          dateSpanLabel += ` + ${f({ id: 'alarms.modal.timeFrame.date' }, { dateFrom, dateTo })} `;
        } else {
          labelForChipInput += ` ${f(
            { id: 'alarms.modal.timeFrame.date' },
            { dateFrom, dateTo }
          )} `;
          dateSpanLabel += ` ${f({ id: 'alarms.modal.timeFrame.date' }, { dateFrom, dateTo })} `;
        }

        valuesForSend = {
          recordId: valuesForChipInput.id,
          label: dateSpanLabel.trim(),
          type: item.timeFrame,
          value: {
            // min: item.dateSpan[0],
            min: dayjs.utc(item.dateSpan[0].toString()).toString(),
            // max: item.dateSpan[1],
            max: dayjs.utc(item.dateSpan[1].toString().replace('00:00:00', '23:59:59')).toString(),
          },
        };

        timeFrames.push(valuesForSend);
      } else if (item.timeFrame === TIME_SPAN) {
        let timeSpanLabel = '';
        const timeFrom = `${getHours(item.timeFrom)}:${getMinutes(item.timeFrom)}:00`;
        const timeTo = `${getHours(item.timeTo)}:${getMinutes(item.timeTo)}:59`;

        if (timeFrames.length > 0) {
          labelForChipInput += ` + ${f(
            { id: 'alarms.modal.timeFrame.time' },
            { timeFrom, timeTo }
          )} `;
          timeSpanLabel += ` + ${f({ id: 'alarms.modal.timeFrame.time' }, { timeFrom, timeTo })} `;
        } else {
          labelForChipInput += ` ${f({ id: 'alarms.modal.timeFrame.time' }, { timeFrom, timeTo })}`;
          timeSpanLabel += ` ${f({ id: 'alarms.modal.timeFrame.time' }, { timeFrom, timeTo })}`;
        }
        valuesForSend = {
          recordId: valuesForChipInput.id,
          label: timeSpanLabel.trim(),
          type: item.timeFrame,
          value: {
            min: timeFrom,
            max: timeTo,
          },
        };

        timeFrames.push(valuesForSend);
      }
    });

    valuesForChipInput.label = labelForChipInput.trim();

    props.responseCallback(valuesForChipInput, timeFrames);

    if (isNextForm) {
      reset();
      props.openCallback();
    } else {
      props.closeCallback();
    }
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'alarms.modal.timeFrame.button.close' }),
      tooltip: f({ id: 'alarms.modal.timeFrame.button.close.tooltip' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'time-frame-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: props.id
        ? f({ id: 'alarms.modal.timeFrame.button.editTimeFrame' })
        : f({ id: 'alarms.modal.timeFrame.button.addTimeFrame' }),
      tooltip: props.id
        ? f({ id: 'alarms.modal.timeFrame.button.editTimeFrame.tooltip' })
        : f({ id: 'alarms.modal.timeFrame.button.addTimeFrame.tooltip' }),
      submitCallback: handleSubmit(handleOnSubmit),
    },
  ];

  const getTime = (timeString: string | number) => {
    const timeArr = String(timeString).split(':');
    if (timeArr.length === 2) {
      return new Date(new Date().setHours(Number(timeArr[0]), Number(timeArr[1]), 0, 0));
    }
    if (timeArr.length === 3) {
      return new Date(
        new Date().setHours(Number(timeArr[0]), Number(timeArr[1]), Number(timeArr[2]), 0)
      );
    }
    return new Date();
  };

  React.useEffect(() => {
    if (props.id && props.data) {
      let timeFrames: ITimeFrame[] = [];
      props.data.forEach((timeFrameGroup: any) => {
        const filteredTimeFrames = timeFrameGroup.filter(
          (item: IAlarmTimeFrame) => String(item.recordId) === String(props.id)
        );
        if (filteredTimeFrames.length > 0) {
          timeFrames = filteredTimeFrames.map((item: IAlarmTimeFrame) => {
            const itemData = {
              timeFrame: item.type,
              dateSpan:
                item.type === DATE_SPAN
                  ? [
                      (item.value as ITimeFrameDateTimeSpan).min,
                      (item.value as ITimeFrameDateTimeSpan).max,
                    ]
                  : '',
              timeFrom:
                item.type === TIME_SPAN ? getTime((item.value as ITimeFrameDateTimeSpan).min) : '',
              timeTo:
                item.type === TIME_SPAN ? getTime((item.value as ITimeFrameDateTimeSpan).max) : '',
              daysOfWeek:
                item.type === DAYS_OF_WEEK
                  ? (item.value as number[]).map((id: number) => String(id))
                  : [],
              days:
                item.type === DAYS_MONTHS
                  ? (item.value as ITimeFrameDaysMonths).days.map((day: number) => String(day))
                  : [],
              months:
                item.type === DAYS_MONTHS
                  ? (item.value as ITimeFrameDaysMonths).months.map((month: number) =>
                      String(month)
                    )
                  : [],
            };
            return itemData;
          });
        }
      });

      timeFrames.forEach((_item: ITimeFrame, index) => {
        update(index, timeFrames[index]);
      });
    }
  }, []);

  const tempDataOptions: Record<string, IOption[]> = {};
  const newOptionsForSelect: Record<string, IOption[]> = {
    0: timeFrameValues,
    1: timeFrameValues,
    2: timeFrameValues,
    3: timeFrameValues,
  };

  const handleAddNewCondition = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['alarm', 'add', 'Add new alarm time frame']);

    append({
      timeFrame: '',
      dateSpan: [],
      timeFrom: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
      timeTo: new Date(new Date().setHours(23, 59, 0, 0)).toISOString(),
      daysOfWeek: [],
      days: [],
      months: [],
    });
  };

  // Set correct number of days after month is selected
  const handleOnChangeMonths = (values: (string | number)[], _index: number) => {
    trackEvent(['alarm', 'change', 'Alarm month changed']);
    let daysInMonth = 0;
    if (values.length === 1) {
      daysInMonth = dayjs(values[0]).daysInMonth();
    }
    if (values.length > 1) {
      values.forEach((month) => {
        const days = dayjs(month).daysInMonth();
        if (daysInMonth === 0) {
          daysInMonth = days;
        }
        if (daysInMonth < days) {
          daysInMonth = days;
        }
      });
    }

    setDaysOfMonthValues(createDays(daysInMonth));
    trigger();
  };

  Object.keys(newOptionsForSelect).forEach((category, index) => {
    tempDataOptions[category] = newOptionsForSelect[category].filter(
      (option) =>
        !watch('timeFrames').find(
          (frame, frameIndex) =>
            frame.timeFrame === option.value && String(frameIndex) !== String(index)
        )
    );
  });

  const nextForm = (value: boolean) => {
    if (!props.id) {
      setIsNextForm(value);
    }
  };

  const customProps: IFormModalCustomProps = {
    modalFooterCheckboxCallback: undefined,
    modalFooterCheckbox: undefined,
    modalContentClass: 'timeframes',
  };
  if (!props.id) {
    customProps.modalFooterCheckboxCallback = nextForm;
    customProps.modalFooterCheckbox = f({ id: 'alarms.modal.timeFrame.nextForm' });
    customProps.modalFooterCheckboxTitle = f({
      id: 'alarms.modal.timeFrame.nextForm.tooltip',
    });
  }
  const handleRemoveCondition = (indexOfArray: number) => {
    remove(indexOfArray);
  };

  return (
    <CmdModal
      id="rule-form-modal"
      title={f({ id: 'alarms.modal.timeFrame.title' })}
      footerButtons={buttons}
      loader={isLoading}
      bodyPaddingClass="p-0"
      {...customProps}
    >
      <>
        {isLoading && <CmdLoader inContent />}
        {fields.map((fieldItem, index) => (
          <div key={fieldItem.id}>
            <div className="p-6" key={fieldItem.id}>
              {!props.id && (
                <Controller
                  name={`timeFrames.${index}.timeFrame`}
                  control={control}
                  render={({ field }) => (
                    <SimpleSelectHookForm
                      {...field}
                      id={`timeFrames.${index}.timeFrame`}
                      // key={fieldItem.id}
                      label={f({ id: 'alarms.modal.timeFrame.timeFrame' })}
                      options={tempDataOptions[index]}
                      onChangeCallback={(value: string) => {
                        setValue(`timeFrames.${index}.timeFrame`, value);
                        // trigger(`timeFrames.${index}.timeFrame`);
                        trigger();
                        // handleTimeFrameOnChange(value, index);
                        trackEvent(['alarm', 'change', 'Alarm time frame changed']);
                      }}
                      required={true}
                      tooltip={
                        watch('timeFrames')[index].timeFrame === DAYS_MONTHS
                          ? f({ id: 'enum.alarms.timeFrames.types.daysMonths.tooltip' })
                          : f({ id: 'alarms.modal.timeFrame.timeFrame.tooltip' })
                      }
                      isAdditionalButton={watch('timeFrames').length !== 1}
                      addtionalButtonCallBack={() => {
                        handleRemoveCondition(index);
                      }}
                      tooltipAdditionalButton={f({
                        id: 'alarms.modal.timeFrame.timeFrame.remove.button.tooltip',
                      })}
                      control={control}
                    />
                  )}
                />
              )}
              {props.id && props.data && (
                <CmdLabel
                  id={`timeFrames.${index}.timeFrame.label`}
                  title={f({ id: 'alarms.modal.timeFrame.timeFrame' })}
                  value={FormikService.getOptionByValue(
                    tempDataOptions[index],
                    watch('timeFrames')[index].timeFrame
                  )}
                  tooltip={
                    watch('timeFrames')[index].timeFrame === DAYS_MONTHS
                      ? f({ id: 'enum.alarms.timeFrames.types.daysMonths.tooltip' })
                      : ''
                  }
                />
              )}
              {watch('timeFrames')[index].timeFrame === DATE_SPAN && (
                <Controller
                  name={`timeFrames.${index}.dateSpan`}
                  // key={fieldItem.id}
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      // key={fieldItem.id}
                      {...field}
                      id={`timeFrames[${index}].dateSpan`}
                      label={f({ id: 'alarms.modal.timeFrame.dateSpan' })}
                      onChangeCallback={(value: Date | Date[]) => {
                        setValue(`timeFrames.${index}.dateSpan`, value as unknown as string[]);
                        trigger();
                        trackEvent(['alarm', 'change', 'Alarm date span changed']);
                      }}
                      // enableTime={false}
                      dateFormat="dd.MM.yyyy"
                      mode="range"
                      // allowedRange={
                      //   timeFrameValidation
                      //     ? getAllowedRange(timeFrameValidation.dateSpan)
                      //     : []
                      // }
                      beforeToday={true}
                      required={
                        timeFrameValidation
                          ? FormikValidatorService.isFieldRequired(timeFrameValidation.dateSpan)
                          : false
                      }
                      disabledItems={[
                        'YESTERDAY',
                        'LAST_WEEK',
                        'THIS_MONTH',
                        'PREVIOUS_MONTH',
                        'THIS_YEAR',
                        'PREVIOUS_YEAR',
                      ]}
                      error={errors.timeFrames && errors.timeFrames[index]?.dateSpan?.message}
                      touched={true}
                    />
                  )}
                />
              )}
              {watch('timeFrames')[index].timeFrame === TIME_SPAN && (
                <>
                  <Controller
                    name={`timeFrames.${index}.timeFrom`}
                    control={control}
                    render={({ field }) => (
                      <Calendar
                        {...field}
                        id={`timeFrames.${index}.timeFrom`}
                        label={f({ id: 'alarms.modal.timeFrame.timeFrom' })}
                        mode="time"
                        enableTime={true}
                        noCalendar={true}
                        // dateFormat="H:i"
                        required={
                          timeFrameValidation
                            ? FormikValidatorService.isFieldRequired(timeFrameValidation.timeSpan)
                            : false
                        }
                        onChangeCallback={(value) => {
                          setValue(`timeFrames.${index}.timeFrom`, value as unknown as string);
                          trigger(`timeFrames.${index}.timeFrom`);
                          trackEvent(['alarm', 'change', 'Alarm time from changed']);
                        }}
                      />
                    )}
                  />
                  <Controller
                    name={`timeFrames.${index}.timeTo`}
                    control={control}
                    render={({ field }) => (
                      <Calendar
                        {...field}
                        id={`timeFrames.${index}.timeTo`}
                        label={f({ id: 'alarms.modal.timeFrame.timeTo' })}
                        mode="time"
                        enableTime={true}
                        noCalendar={true}
                        // dateFormat="H:i"
                        required={
                          timeFrameValidation
                            ? FormikValidatorService.isFieldRequired(timeFrameValidation.timeSpan)
                            : false
                        }
                        onChangeCallback={(value) => {
                          setValue(`timeFrames.${index}.timeTo`, value as unknown as string);
                          trigger(`timeFrames.${index}.timeTo`);
                          trackEvent(['alarm', 'change', 'Alarm time to changed']);
                        }}
                      />
                    )}
                  />
                </>
              )}
              {watch('timeFrames')[index].timeFrame === DAYS_OF_WEEK && (
                <Controller
                  name={`timeFrames.${index}.daysOfWeek`}
                  control={control}
                  render={({ field }) => (
                    <CheckboxGroupArrayHookForm
                      {...field}
                      label={f({ id: 'alarms.modal.timeFrame.addDays' })}
                      options={daysOfWeekValues}
                      required={
                        timeFrameValidation
                          ? FormikValidatorService.isFieldRequired(timeFrameValidation.daysOfWeek)
                          : false
                      }
                      control={control}
                      onChangeCallback={(value) => {
                        setValue(`timeFrames.${index}.daysOfWeek`, value);
                        trigger();
                        trackEvent(['alarm', 'change', 'Alarm days of week changed']);
                      }}
                    />
                  )}
                />
              )}
              {watch('timeFrames')[index].timeFrame === DAYS_MONTHS && (
                <>
                  <Controller
                    name={`timeFrames.${index}.months`}
                    control={control}
                    render={({ field }) => (
                      <MultiSelectHookForm
                        {...field}
                        id={`timeFrames.${index}.months`}
                        label={f({ id: 'alarms.modal.timeFrame.months' })}
                        options={monthValues}
                        required={
                          timeFrameValidation
                            ? FormikValidatorService.isFieldRequired(
                                timeFrameValidation['daysMonths.months']
                              )
                            : false
                        }
                        selectAllButton={f({
                          id: 'alarms.modal.timeFrame.button.selectAll',
                        })}
                        selectButton={f({ id: 'alarms.modal.timeFrame.button.select' })}
                        resetButton={f({ id: 'alarms.modal.timeFrame.button.reset' })}
                        selectButtonTooltip={f({
                          id: 'alarms.modal.timeFrame.months.button.select.tooltip',
                        })}
                        resetButtonTooltip={f({
                          id: 'alarms.modal.timeFrame.months.button.reset.tooltip',
                        })}
                        onChangeCallback={(value) => handleOnChangeMonths(value, index)}
                        control={control}
                      />
                    )}
                  />
                  <Controller
                    name={`timeFrames.${index}.days`}
                    control={control}
                    render={({ field }) => (
                      <MultiSelectHookForm
                        {...field}
                        id={`timeFrames.${index}.days`}
                        label={f({ id: 'alarms.modal.timeFrame.days' })}
                        options={daysOfMonthValues}
                        required={
                          timeFrameValidation
                            ? FormikValidatorService.isFieldRequired(
                                timeFrameValidation['daysMonths.days']
                              )
                            : false
                        }
                        selectAllButton={f({
                          id: 'alarms.modal.timeFrame.button.selectAll',
                        })}
                        selectButton={f({ id: 'alarms.modal.timeFrame.button.select' })}
                        resetButton={f({ id: 'alarms.modal.timeFrame.button.reset' })}
                        selectButtonTooltip={f({
                          id: 'alarms.modal.timeFrame.days.button.select.tooltip',
                        })}
                        resetButtonTooltip={f({
                          id: 'alarms.modal.timeFrame.days.button.reset.tooltip',
                        })}
                        valuesInColumns={true}
                        control={control}
                        onChangeCallback={(value) => {
                          setValue(`timeFrames.${index}.days`, value);
                          trigger();
                          trackEvent(['alarm', 'change', 'Alarm days changed']);
                        }}
                      />
                    )}
                  />
                </>
              )}
            </div>
            {watch('timeFrames').length - 1 !== index && <hr className="m-0" />}
          </div>
        ))}
        {!props.id && watch('timeFrames').length < 4 && (
          <div className="text-right" style={{ padding: '0 32px 24px 32px', marginTop: '-8px' }}>
            <a
              href="#"
              className={`e-button ${watch('timeFrames').length >= 4 ? ' disabled' : ''}`}
              onClick={handleAddNewCondition}
              title={f({ id: 'alarms.modal.timeFrame.addNewCondition.tooltip' })}
              data-cy="add-new-conditions"
            >
              {/* <i className="cmd-icon cmd-icon-add mr-2" /> */}
              {f({ id: 'alarms.modal.timeFrame.addNewCondition' })}
            </a>
          </div>
        )}
      </>
    </CmdModal>
  );
}
